// DEPENDENCIES ---------------------------------------------------------------- //

import { Smsmessages } from '@gql/base-types';

// TYPES ---------------------------------------------------------------- //

interface MoveReadStatus {
  hasUnread: boolean;
  lastReadMessageId: string | null;
  lastReadTime: string | null;
  lastMessageId: string | null;
  lastMessageTime: string | null;
  lastMessageDirection: 'inbound' | 'outbound' | null;
  messageCount: number;
  lastChecked: string;
}

interface ReadStatusMap {
  [moveId: string]: MoveReadStatus;
}

// CONSTANTS ---------------------------------------------------------------- //

const STORAGE_KEY = 'sms-read-status';
const STORAGE_VERSION = '2.0'; // Version tracking for migration
const VERSION_KEY = 'sms-read-status-version';

// MIGRATION ---------------------------------------------------------------- //

/**
 * Migrate from old response tracker to new read status tracker
 */
const migrateFromOldSystem = (): void => {
  try {
    // Check if we need to migrate
    const currentVersion = localStorage.getItem(VERSION_KEY);
    if (currentVersion === STORAGE_VERSION) {
      return; // Already migrated
    }

    // Clear old data
    const oldKey = 'sms-response-status';
    if (localStorage.getItem(oldKey)) {
      localStorage.removeItem(oldKey);
    }

    // Set new version
    localStorage.setItem(VERSION_KEY, STORAGE_VERSION);
  } catch (error) {
    console.error('Error migrating SMS read status:', error);
  }
};

// Initialize migration on module load
migrateFromOldSystem();

// HELPER FUNCTIONS ---------------------------------------------------------------- //

/**
 * Get all read statuses from localStorage
 */
export const getAllReadStatuses = (): ReadStatusMap => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading SMS read status from localStorage:', error);
    return {};
  }
};

/**
 * Save read statuses to localStorage
 */
export const saveReadStatuses = (statuses: ReadStatusMap): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(statuses));
  } catch (error) {
    console.error('Error saving SMS read status to localStorage:', error);
  }
};

/**
 * Get read status for a specific move
 */
export const getMoveReadStatus = (moveId: number | undefined): MoveReadStatus | null => {
  if (!moveId) return null;
  
  const statuses = getAllReadStatuses();
  return statuses[moveId.toString()] || null;
};

/**
 * Check if a move has unread messages
 */
export const hasUnreadMessages = (moveId: number | undefined): boolean => {
  const status = getMoveReadStatus(moveId);
  return status?.hasUnread || false;
};

/**
 * Derive a move's next read status from its messages and its previous status.
 *
 * Pure — takes the previous status in, touches no storage, and returns null when
 * the stored status is already current. Returning null is what lets callers skip
 * the write entirely, preserving the cross-tab fix below.
 */
const deriveMoveReadStatus = (
  currentStatus: MoveReadStatus | null,
  messages: Smsmessages[]
): MoveReadStatus | null => {
  // Sort messages by created date (newest first)
  const sortedMessages = [...messages].sort((a, b) => {
    const dateA = new Date(a.createdat || 0).getTime();
    const dateB = new Date(b.createdat || 0).getTime();
    return dateB - dateA;
  });

  // Find the last message
  const lastMessage = sortedMessages[0];
  const lastMessageId = lastMessage?.id ? String(lastMessage.id) : null;
  const lastMessageTime = lastMessage?.createdat || null;
  const lastMessageDirection = lastMessage?.direction as 'inbound' | 'outbound' | null;

  
  // If we already have a status and the last message hasn't changed, keep the existing status.
  // Don't write back to localStorage — refreshing lastChecked on every call causes a cross-tab
  // storage-event storm (each tab's render writes, every other tab reacts, ping-pong loops).
  if (currentStatus && currentStatus.lastMessageId === lastMessageId) {
    return null;
  }
  
  // Preserve the lastReadMessageId from the current status
  const lastReadMessageId = currentStatus?.lastReadMessageId || null;
  
  // Determine if there are unread messages
  // Messages are unread if:
  // 1. There's a new message (different ID than last read)
  // 2. We've never read any messages from this move
  // 3. BUT if the last message direction is outbound (dealer sent), consider it read
  const hasUnread = lastMessageId !== null && 
                    lastMessageId !== lastReadMessageId &&
                    lastMessageDirection !== 'outbound'; // Don't mark as unread if dealer sent last message

  const status: MoveReadStatus = {
    hasUnread,
    lastReadMessageId: currentStatus?.lastReadMessageId || null, // Preserve existing read status
    lastReadTime: currentStatus?.lastReadTime || null, // Preserve existing read time
    lastMessageId,
    lastMessageTime,
    lastMessageDirection,
    lastChecked: new Date().toISOString(),
    messageCount: messages.length,
  };

  return status;
};

/**
 * Update read status for a move based on its messages
 */
export const updateMoveReadStatus = (
  moveId: number,
  messages: Smsmessages[]
): MoveReadStatus => {
  const statuses = getAllReadStatuses();
  const key = moveId.toString();
  const next = deriveMoveReadStatus(statuses[key] || null, messages);

  // Unchanged: return the stored status without writing (see cross-tab note above).
  if (!next) return statuses[key];

  statuses[key] = next;
  saveReadStatuses(statuses);
  return next;
};

/**
 * Batch equivalent of updateMoveReadStatus: reads localStorage once, folds every
 * move's status into the map in memory, and writes at most once — only if some
 * status actually changed. Returns the resulting map so the caller can read each
 * status back without going to storage again.
 *
 * Use this whenever updating more than one move. Calling the single-move version in
 * a loop costs a full JSON.parse of the ENTIRE status map per move (twice, via
 * getMoveReadStatus then getAllReadStatuses) plus a full JSON.stringify to save it —
 * O(n^2) synchronous, disk-backed work on the main thread. On a dashboard page of
 * ~100 moves with messages that is enough to stop click events dispatching while the
 * page still paints and scrolls.
 */
export const updateMoveReadStatuses = (
  entries: { moveId: number; messages: Smsmessages[] }[]
): ReadStatusMap => {
  const statuses = getAllReadStatuses();
  let changed = false;

  entries.forEach(({ moveId, messages }) => {
    const key = moveId.toString();
    const next = deriveMoveReadStatus(statuses[key] || null, messages);
    if (next) {
      statuses[key] = next;
      changed = true;
    }
  });

  if (changed) saveReadStatuses(statuses);
  return statuses;
};

/**
 * Mark messages as read for a move
 */
export const markMessagesAsRead = (moveId: number, lastMessageId?: string): void => {
  const statuses = getAllReadStatuses();
  const currentStatus = statuses[moveId.toString()];
  
  if (currentStatus) {
    // Use provided lastMessageId or current lastMessageId
    const messageIdToMark = lastMessageId || currentStatus.lastMessageId;
    
    statuses[moveId.toString()] = {
      ...currentStatus,
      hasUnread: false,
      lastReadMessageId: messageIdToMark,
      lastReadTime: new Date().toISOString(),
      lastChecked: new Date().toISOString(),
    };
    saveReadStatuses(statuses);
    
    // Dispatch custom event for cross-component updates
    window.dispatchEvent(new CustomEvent('sms-read-status-changed', {
      detail: { moveId, hasUnread: false }
    }));
  } else if (lastMessageId) {
    // Create new status entry if it doesn't exist
    statuses[moveId.toString()] = {
      hasUnread: false,
      lastReadMessageId: lastMessageId,
      lastReadTime: new Date().toISOString(),
      lastMessageId: lastMessageId,
      lastMessageTime: new Date().toISOString(),
      lastMessageDirection: null,
      lastChecked: new Date().toISOString(),
      messageCount: 0,
    };
    saveReadStatuses(statuses);
    
    window.dispatchEvent(new CustomEvent('sms-read-status-changed', {
      detail: { moveId, hasUnread: false }
    }));
  }
};

/**
 * Mark a move as having new unread messages (when customer sends a message)
 */
export const markMoveAsUnread = (moveId: number, messageId: string): void => {
  const statuses = getAllReadStatuses();
  const currentStatus = statuses[moveId.toString()];
  
  if (currentStatus) {
    statuses[moveId.toString()] = {
      ...currentStatus,
      hasUnread: true,
      lastMessageId: messageId,
      lastMessageTime: new Date().toISOString(),
      lastMessageDirection: 'inbound',
      lastChecked: new Date().toISOString(),
    };
  } else {
    // Create new status entry
    statuses[moveId.toString()] = {
      hasUnread: true,
      lastReadMessageId: null,
      lastReadTime: null,
      lastMessageId: messageId,
      lastMessageTime: new Date().toISOString(),
      lastMessageDirection: 'inbound',
      lastChecked: new Date().toISOString(),
      messageCount: 1,
    };
  }
  
  saveReadStatuses(statuses);
  
  window.dispatchEvent(new CustomEvent('sms-read-status-changed', {
    detail: { moveId, hasUnread: true }
  }));
};

/**
 * Clear read status for a move
 */
export const clearMoveReadStatus = (moveId: number): void => {
  const statuses = getAllReadStatuses();
  delete statuses[moveId.toString()];
  saveReadStatuses(statuses);
};

/**
 * Clear all read statuses (useful for logout)
 */
export const clearAllReadStatuses = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(VERSION_KEY);
  } catch (error) {
    console.error('Error clearing SMS read statuses:', error);
  }
};

/**
 * Get count of moves with unread messages
 */
export const getUnreadCount = (): number => {
  const statuses = getAllReadStatuses();
  return Object.values(statuses).filter(status => status.hasUnread).length;
};

/**
 * Get list of move IDs with unread messages
 */
export const getUnreadMoveIds = (): number[] => {
  const statuses = getAllReadStatuses();
  return Object.entries(statuses)
    .filter(([_, status]) => status.hasUnread)
    .map(([moveId]) => parseInt(moveId, 10));
};

/**
 * Clean up entries whose conversation went quiet more than 45 days ago.
 *
 * Prunes on the conversation's last activity (lastMessageTime), NOT on
 * lastChecked: unchanged statuses are deliberately never re-written (see the
 * cross-tab note in deriveMoveReadStatus), so lastChecked goes stale on idle
 * conversations. The window must also stay wider than the dashboard's 30-day
 * message query (useUnreadMessages) — pruning an entry the dashboard can still
 * re-derive resets lastReadMessageId to null and flips a read conversation
 * back to unread.
 */
export const cleanupOldStatuses = (): void => {
  const statuses = getAllReadStatuses();
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 45);

  const cleaned = Object.entries(statuses).reduce((acc, [moveId, status]) => {
    const lastActivity = new Date(status.lastMessageTime || status.lastChecked);
    if (lastActivity > cutoff) {
      acc[moveId] = status;
    }
    return acc;
  }, {} as ReadStatusMap);

  saveReadStatuses(cleaned);
};

// EXPORT ---------------------------------------------------------------- //

export default {
  getAllReadStatuses,
  saveReadStatuses,
  getMoveReadStatus,
  hasUnreadMessages,
  updateMoveReadStatus,
  markMessagesAsRead,
  markMoveAsUnread,
  clearMoveReadStatus,
  clearAllReadStatuses,
  getUnreadCount,
  getUnreadMoveIds,
  cleanupOldStatuses,
};